/* SPEC-463 — the right context panel's pure rules: when it docks, whether it starts open on
 * a page, how the per-page choice is remembered, and which keypress toggles it.
 *
 * Pure, no React, no `@/` alias — asserted by `config/contextPanel.test.ts` under Node's
 * type stripping (the same pattern as `shell.ts`).
 *
 * Panel contract (frontend/src/CONTEXT.md):
 *   - ONE panel, right of the page. A page OPTS IN; a page that does not register gets no
 *     panel and loses no width.
 *   - Each section renders only when it has real content. With none, the panel shows the
 *     page's own one-line hint — never an empty card.
 *   - It never squeezes the page below MIN_MAIN_WIDTH. Without that room it does not dock,
 *     and page-specific content renders inline where the page placed it.
 */

/** Open width of the docked panel (px). The spec range is 320–360. */
export const PANEL_WIDTH = 336;
/** Width of the collapsed strip that holds the open button (px). */
export const STRIP_WIDTH = 40;
/** The narrowest the page column may become beside the open panel (px). */
export const MIN_MAIN_WIDTH = 640;

/** Can the panel dock beside the page? `rowWidth` is the measured width the page and the
 *  panel share. Zero or a bad reading never docks: no measurement is not room. */
export function canDock(rowWidth: number): boolean {
  if (!Number.isFinite(rowWidth) || rowWidth <= 0) return false;
  return rowWidth >= PANEL_WIDTH + MIN_MAIN_WIDTH;
}

/** Dense table pages: the panel starts CLOSED so the data keeps its width. */
export const DENSE_TABLE_PAGES: readonly string[] = ['/forecast-workbench', '/demand-planning'];
/** Pages that already had a right panel before SPEC-463: the panel starts OPEN. */
export const OPEN_BY_DEFAULT_PAGES: readonly string[] = ['/governance', '/box-a'];

/** The page key for a pathname: the path without a trailing slash. Query strings never
 *  reach here (tabs share their page's choice). */
export function pageKey(pathname: string): string {
  const p = (pathname || '/').split('?')[0].split('#')[0];
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p || '/';
}

/** Where the panel starts on a page nobody has toggled yet. Dense pages and every page not
 *  listed start closed: an unrequested panel costs width. */
export function defaultOpen(pathname: string): boolean {
  const key = pageKey(pathname);
  if (DENSE_TABLE_PAGES.includes(key)) return false;
  return OPEN_BY_DEFAULT_PAGES.includes(key);
}

export const STORAGE_KEY = 'suryaiq.contextPanel.open.v1';

/** Parse the stored per-page map. Anything malformed reads as "nothing stored" — a bad
 *  value must never throw on page load or stick a page open. */
export function parseStored(raw: string | null): Record<string, boolean> {
  if (!raw) return {};
  try {
    const v: unknown = JSON.parse(raw);
    if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
    const out: Record<string, boolean> = {};
    for (const [k, b] of Object.entries(v as Record<string, unknown>)) {
      if (typeof b === 'boolean') out[k] = b;
    }
    return out;
  } catch {
    return {};
  }
}

/** Open or closed on this page: the reader's remembered choice, else the page default. */
export function resolveOpen(stored: Record<string, boolean>, pathname: string): boolean {
  const key = pageKey(pathname);
  return Object.prototype.hasOwnProperty.call(stored, key) ? stored[key] : defaultOpen(pathname);
}

/** The toggle shortcut is `]` with no modifier. It never fires while the reader types. */
export const TOGGLE_KEY = ']';

export function isToggleShortcut(e: {
  key: string; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean;
  targetTag?: string | null; targetEditable?: boolean;
}): boolean {
  if (e.key !== TOGGLE_KEY) return false;
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  const tag = (e.targetTag || '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return false;
  if (e.targetEditable) return false;
  return true;
}
